import React, { useState, useEffect } from 'react'
import { useSearchParams, useNavigate } from 'react-router-dom'
import axios from '../config/axios'
import { FiAlertCircle } from 'react-icons/fi'
import PinInput from '../components/PinInput'

function ResetPinPage() {
  const [searchParams] = useSearchParams()
  const navigate = useNavigate()
  const token = searchParams.get('token')

  const [pin, setPin] = useState('')
  const [confirmPin, setConfirmPin] = useState('')
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  const [done, setDone] = useState(false)

  useEffect(() => {
    if (!token) setError('Invalid reset link. Please request a new one.')
  }, [token])

  const handleSubmit = async (e) => {
    e.preventDefault()
    setError('')
    if (pin.length < 6) { setError('Please enter a 6-digit PIN'); return }
    if (pin !== confirmPin) { setError('PINs do not match'); return }
    setLoading(true)
    try {
      await axios.post('/api/auth/reset-pin', { token, newPin: pin })
      setDone(true)
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to reset PIN. The link may have expired.')
    } finally {
      setLoading(false)
    }
  }

  return (
    <main className="min-h-screen bg-bg-dark flex items-center justify-center px-4">
      <div className="w-full max-w-md">

        <div className="text-center mb-8">
          <h1 className="text-4xl font-bold text-accent-yellow mb-2">Case Sizing Engine</h1>
          <p className="text-accent-yellow">Input your specs, we do the work. You get the perfect case!</p>
        </div>

        <div className="card">
          {done ? (
            <div className="text-center space-y-4">
              <div className="text-4xl">✓</div>
              <p className="text-white font-semibold">PIN reset successfully</p>
              <p className="text-gray-400 text-sm">You can now sign in with your new 6-digit PIN.</p>
              <button onClick={() => navigate('/auth')} className="w-full btn-primary mt-2">
                Go to Sign In
              </button>
            </div>
          ) : (
            <>
              <h2 className="text-2xl font-bold text-white mb-2">Reset Your PIN</h2>
              <p className="text-gray-400 text-sm mb-6">Choose a new 6-digit PIN for your account.</p>

              {error && (
                <div className="mb-6 p-4 bg-red-900/20 border border-red-800 rounded flex gap-3 text-red-200">
                  <FiAlertCircle className="flex-shrink-0 mt-0.5" />
                  <p className="text-sm">{error}</p>
                </div>
              )}

              <form onSubmit={handleSubmit} className="space-y-5">
                <div>
                  <label className="block text-sm font-semibold mb-3 text-accent-yellow text-center">New 6-Digit PIN</label>
                  <PinInput value={pin} onChange={setPin} disabled={loading || !token} />
                </div>
                <div>
                  <label className="block text-sm font-semibold mb-3 text-accent-yellow text-center">Confirm New PIN</label>
                  <PinInput value={confirmPin} onChange={setConfirmPin} disabled={loading || !token} />
                </div>
                <button
                  type="submit"
                  disabled={loading || !token}
                  className="w-full btn-primary mt-2 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                >
                  {loading ? (
                    <>
                      <svg className="animate-spin h-4 w-4 flex-shrink-0" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z" />
                      </svg>
                      Resetting…
                    </>
                  ) : 'Set New PIN'}
                </button>
                <button type="button" onClick={() => navigate('/auth')} className="w-full text-sm text-gray-500 hover:text-gray-300 transition mt-1">
                  Back to Sign In
                </button>
              </form>
            </>
          )}
        </div>

        <p className="text-center text-gray-500 text-xs mt-8">© 2026 - Case Sizing Engine</p>
      </div>
    </main>
  )
}

export default ResetPinPage
